import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Tags, X } from "lucide-react";
import { api, idr } from "../../../lib/api";
import { DatePicker } from "../../../components/ui/date-picker";
import InputRupiah from "../../../components/InputRupiah";
import { JENIS, LABEL_JENIS } from "./halamanPP";

// "Jadikan Program Support Aktivasi" / "Tetapkan: Support Cashback" untuk
// program LAMA (brief 22 Sep A2; PP2). Mode yang boleh dipakai, alasannya kalau
// tidak, IMEI yang akan bentrok, dan saran nominal SEMUANYA dari backend
// (`GET /cashback/programs/{id}/tetapkan-jenis`) — aturan yang SAMA dengan
// penyimpanannya, jadi layar tidak menawarkan pilihan yang lalu ditolak.
export default function TetapkanJenisModal({ program, jenis, cbBase, onClose, onSelesai }) {
  const [pra, setPra] = useState(null);
  const [galat, setGalat] = useState(null);
  const [mode, setMode] = useState("");
  const [kanal, setKanal] = useState("");
  const [nominal, setNominal] = useState({});
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [deadline, setDeadline] = useState("");
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let batal = false;
    api.get(`${cbBase}/programs/${program.id}/tetapkan-jenis`, { params: { jenis } })
      .then((r) => {
        if (batal) return;
        setPra(r.data);
        const bisa = (r.data.mode || []).find((m) => m.bisa);
        setMode(bisa ? bisa.mode : "");
        setDari(r.data.periode_aktivasi_dari || "");
        setSampai(r.data.periode_aktivasi_sampai || "");
        setDeadline(r.data.deadline_submit || "");
      })
      .catch((e) => { if (!batal) setGalat(e?.response?.data?.detail || "Gagal memuat pilihan penetapan jenis"); });
    return () => { batal = true; };
  }, [cbBase, program.id, jenis]);

  const pilihan = useMemo(() => (pra?.mode || []).find((m) => m.mode === mode), [pra, mode]);
  const penuh = mode === "penuh";
  const aktivasi = jenis === JENIS.AKTIVASI;
  const saran = pra?.saran_nominal || [];
  const nominalLengkap = saran.length > 0 && saran.every((s) => Number(nominal[s.sku]) > 0);
  const siap = Boolean(pilihan && pilihan.bisa && alasan.trim()
    && (!penuh || (kanal && (!aktivasi || (nominalLengkap && dari && sampai && deadline)))));

  const simpan = async () => {
    if (!siap) return;
    setBusy(true);
    try {
      const body = { jenis_program: jenis, mode, alasan: alasan.trim() };
      if (penuh) {
        body.kanal = kanal;
        if (aktivasi) {
          body.items = saran.map((s) => ({ sku: s.sku, nominal_support: Number(nominal[s.sku]) }));
          body.periode_aktivasi_dari = dari;
          body.periode_aktivasi_sampai = sampai;
          body.deadline_submit = deadline;
        }
      }
      const r = await api.post(`${cbBase}/programs/${program.id}/tetapkan-jenis`, body);
      toast.success(r.data.message);
      onSelesai?.(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menetapkan jenis program"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="cb-tetapkan-modal"
           className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Tags className="w-5 h-5 text-emerald-600" />
            {aktivasi ? "Jadikan Program Support Aktivasi" : "Tetapkan: Support Cashback"} · {program.code}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4 text-sm">
          {galat && <p className="text-rose-700" data-testid="cb-tetapkan-galat">{galat}</p>}
          {!pra && !galat && <p className="text-slate-500">Memuat pilihan…</p>}
          {pra && (
            <>
              <p className="text-slate-600">
                Program lama ini belum punya jenis, jadi klaimnya mengunci Support Aktivasi <b>dan</b> Support
                Cashback sekaligus. Menetapkan jenis {LABEL_JENIS[jenis]} membuat klaimnya hanya mengunci jenis itu.
              </p>
              <div className="space-y-2">
                {pra.mode.map((m) => (
                  <label key={m.mode} data-testid={`cb-tetapkan-mode-${m.mode}`}
                         className={`flex items-start gap-2 border rounded-lg p-3 ${m.bisa ? "border-slate-200 cursor-pointer" : "border-slate-100 bg-slate-50 text-slate-400"}`}>
                    <input type="radio" name="mode-tetapkan" value={m.mode} checked={mode === m.mode} disabled={!m.bisa}
                           onChange={() => setMode(m.mode)} className="mt-1" />
                    <span>
                      <span className="font-medium text-slate-800">{m.label}</span>
                      {!m.bisa && m.alasan && <span className="block text-xs text-slate-500 mt-0.5">{m.alasan}</span>}
                    </span>
                  </label>
                ))}
              </div>
              {pra.jumlah_konflik > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 space-y-1" data-testid="cb-tetapkan-konflik">
                  <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />
                    {pra.jumlah_konflik} IMEI juga punya klaim aktif {LABEL_JENIS[jenis]} di program lain — batalkan salah satunya dulu:</p>
                  {pra.konflik.map((k) => <p key={`${k.imei}-${k.program_code}`}>• {k.imei} · {k.program_code} {k.batch_code || ""}</p>)}
                </div>
              )}
              {penuh && (
                <div className="space-y-3 border-t border-slate-100 pt-3">
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Kanal</span>
                    <select value={kanal} onChange={(e) => setKanal(e.target.value)} data-testid="cb-tetapkan-kanal"
                            className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2">
                      <option value="">— pilih kanal —</option>
                      {pra.kanal.map((k) => <option key={k.kunci} value={k.kunci}>{k.label}</option>)}
                    </select>
                  </label>
                  {aktivasi && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <label className="block"><span className="text-xs font-medium text-slate-600">Periode Aktivasi Dari</span>
                          <DatePicker value={dari} onChange={setDari} data-testid="cb-tetapkan-dari" /></label>
                        <label className="block"><span className="text-xs font-medium text-slate-600">Periode Aktivasi Sampai</span>
                          <DatePicker value={sampai} onChange={setSampai} data-testid="cb-tetapkan-sampai" /></label>
                        <label className="block"><span className="text-xs font-medium text-slate-600">Deadline Aktivasi</span>
                          <DatePicker value={deadline} onChange={setDeadline} data-testid="cb-tetapkan-deadline" /></label>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-600">Nominal Support Aktivasi per SKU (isi dari memo supplier)</p>
                        {saran.map((s, i) => (
                          <div key={s.sku} className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-5">
                              <p className="font-mono text-xs">{s.sku}</p>
                              <p className="text-[11px] text-slate-500">{s.nama_produk}</p>
                            </div>
                            <div className="col-span-4">
                              <InputRupiah value={nominal[s.sku] ?? ""} onChange={(n) => setNominal((p) => ({ ...p, [s.sku]: n }))}
                                           placeholder="Nominal support" testid={`cb-tetapkan-nominal-${i}`}
                                           className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" />
                            </div>
                            <p className="col-span-3 text-[11px] text-slate-500">
                              {s.saran != null ? `Selisih harga lama: ${idr(s.saran)} (saran, bukan diisi otomatis)` : ""}
                            </p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Alasan (wajib — mis. nomor memo supplier)</span>
                <textarea rows={2} value={alasan} onChange={(e) => setAlasan(e.target.value)} data-testid="cb-tetapkan-alasan"
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
              </label>
            </>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">Batal</button>
          <button onClick={simpan} disabled={busy || !siap} data-testid="cb-tetapkan-simpan"
                  className="h-10 px-4 rounded-lg text-white text-sm font-medium disabled:opacity-50 bg-emerald-600 hover:bg-emerald-700">
            {busy ? "Memproses..." : "Tetapkan Jenis"}
          </button>
        </div>
      </div>
    </div>
  );
}
